import * as d3 from 'd3';
import { GroupHistogramData } from '../../../types/histogram.types';
import { createDefaultCategoricalScale } from '../../constants/palette-utils';
import { COLORS } from '../../constants/colors';

interface RenderDensityOptions {
  chartGroup: d3.Selection<SVGGElement, unknown, null, undefined>;
  showDensity: boolean;
  densityData: [number, number][];
  groupDensityData: { groupName: string; densityData: [number, number][] }[];
  densityScale: number;
  isGrouped: boolean;
  groupMode: 'overlay' | 'stacked' | 'faceted';
  groupHistData?: GroupHistogramData[];
  orientation: 'vertical' | 'horizontal';
  currentXScale: d3.ScaleLinear<number, number>;
  currentYScale: d3.ScaleLinear<number, number>;
  xScale: d3.ScaleLinear<number, number>;
  yScale: d3.ScaleLinear<number, number>;
  bins: number;
  originalXDomain: [number, number];
}

/**
 * 密度曲線を描画する関数
 */
export const renderDensityCurves = ({
  chartGroup,
  showDensity,
  densityData,
  groupDensityData,
  densityScale,
  isGrouped,
  groupMode,
  groupHistData,
  orientation,
  currentXScale,
  currentYScale,
  xScale,
  yScale,
  bins,
  originalXDomain
}: RenderDensityOptions): void => {
  // 既存の密度曲線を削除
  chartGroup.selectAll('.density-line').remove();
  chartGroup.selectAll('[class^="density-line-group-"]').remove();

  if (!showDensity) {
    return;
  }

  if (isGrouped && groupDensityData.length > 0 && groupMode === 'overlay') {
    // グループ化モードの密度曲線
    const colorScale = createDefaultCategoricalScale();

    groupDensityData.forEach((group, index) => {
      const groupColor = colorScale(group.groupName);
      const groupData = groupHistData?.[index];
      if (!groupData) return;

      // 各グループのデータ数とビン幅から密度スケールを計算
      const binWidth = (originalXDomain[1] - originalXDomain[0]) / bins;
      const groupDensityScale = groupData.originalData.length * binWidth;

      const line = orientation === 'horizontal'
        ? d3.line<[number, number]>()
            .x(d => xScale(d[1] * groupDensityScale))
            .y(d => currentYScale(d[0]))
            .curve(d3.curveBasis)
        : d3.line<[number, number]>()
            .x(d => currentXScale(d[0]))
            .y(d => yScale(d[1] * groupDensityScale))
            .curve(d3.curveBasis);

      chartGroup.append('path')
        .datum(group.densityData)
        .attr('class', `density-line-group-${index}`)
        .attr('fill', 'none')
        .attr('stroke', groupColor)
        .attr('stroke-width', 2)
        .attr('opacity', 0.8)
        .attr('d', line);
    });
  } else if (!isGrouped && densityData.length > 0) {
    // 非グループ化モードの密度曲線
    const line = orientation === 'horizontal'
      ? d3.line<[number, number]>()
          .x(d => xScale(d[1] * densityScale))
          .y(d => currentYScale(d[0]))
          .curve(d3.curveBasis)
      : d3.line<[number, number]>()
          .x(d => currentXScale(d[0]))
          .y(d => yScale(d[1] * densityScale))
          .curve(d3.curveBasis);

    chartGroup.append('path')
      .datum(densityData)
      .attr('class', 'density-line')
      .attr('fill', 'none')
      .attr('stroke', COLORS.visualization.densityLine)
      .attr('stroke-width', 2)
      .attr('d', line);
  }
};
